import React, { useState } from 'react';
import { Box, Chip, colors, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import {
  getLabourRates,
  Role,
  LabourCostRate,
  LabourCostRateUserOverride,
  LabourBillingRate,
} from 'api/labourApi';
import { LabourRatesTableComponent } from './LabourRatesTableComponent';
import { WorkingAwayFromHomeRatesTableComponent } from './WorkingAwayFromHomeRatesTableComponent';
import LabourCostRateUserOverrideTable from './LabourCostRateUserOverrideTable';
import AddLabourRates from './AddLabourRates';
import LabourDetailsView from './LabourDetailsView';
import AddNewRateCardItemButton from '../EquipmentSection/AddNewRateCardItemButton';
import { blueGrey } from '@mui/material/colors';
import LabourCostOverrideDetailsView from './LabourCostOverrideDetailsView';
import ImportLabourRoleRatesMenuButton, {
  ImportRoleRatesScope,
} from './ImportLabourRoleRatesMenuButton';
import BulkUpdateRoleRatesDialog from './BulkUpdateRoleRatesDialog';
import BulkUpdateScopedBillingRatesDialog from './BulkUpdateScopedBillingRatesDialog';
import BulkUpdateEmployeeCostRatesDialog from './BulkUpdateEmployeeCostRatesDialog';

enum RateType {
  COST = 'cost',
  BILLING = 'billing',
}

enum LabourRateModalsType {
  COST_RATE = 'costRate',
  COST_RATE_OVERRIDE = 'costRateOverride',
  BILLING_RATE = 'billingRate',
  DETAILS_VIEW = 'detailsView',
  COST_OVERRIDE_VIEW = 'costOverrideView',
}

export default function LabourRateCardHomePage() {
  const [selectedTableType, setSelectedTableType] = useState<
    'roleRates' | 'userOverrides'
  >('userOverrides');
  const [importRoleRatesScope, setImportRoleRatesScope] =
    useState<ImportRoleRatesScope | null>(null);
  const [importEmployeeCostOpen, setImportEmployeeCostOpen] = useState(false);
  const [modalState, setModalState] = useState({
    [LabourRateModalsType.COST_RATE]: false,
    [LabourRateModalsType.COST_RATE_OVERRIDE]: false,
    [LabourRateModalsType.BILLING_RATE]: false,
    [LabourRateModalsType.DETAILS_VIEW]: false,
    [LabourRateModalsType.COST_OVERRIDE_VIEW]: false,
  });
  const [selectedRole, setSelectedRole] = useState<Role | undefined>(undefined);
  const [selectedUserCostOverride, setSelectedUserCostOverride] =
    useState<LabourCostRateUserOverride | null>(null);
  const [selectedRateType, setSelectedRateType] = useState<RateType>(
    RateType.COST
  );
  const [editCostRate, setEditCostRate] = useState<LabourCostRate | null>(null);
  const [editCostRateOverride, setEditCostRateOverride] =
    useState<LabourCostRateUserOverride | null>(null);
  const [editBillingRate, setEditBillingRate] =
    useState<LabourBillingRate | null>(null);

  const openModal = (key: LabourRateModalsType) =>
    setModalState((prev) => ({ ...prev, [key]: true }));

  const closeModal = (key: LabourRateModalsType) =>
    setModalState((prev) => ({ ...prev, [key]: false }));

  const labourData = useQuery(['labour-rates'], getLabourRates);

  const handleRoleClick = (role: Role, type: RateType) => {
    setSelectedRole(role);
    setSelectedRateType(type);
    openModal(LabourRateModalsType.DETAILS_VIEW);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          overflowY: 'auto',
          m: 3,
        }}>
        <Box
          style={{
            display: 'inline-grid',
            justifyContent: 'center',
          }}>
          <Chip
            variant="outlined"
            label="Cost Rates by Employee"
            style={{
              fontWeight: 'bold',
              marginInline: 5,
              color: selectedTableType === 'userOverrides' ? 'white' : 'black',
              border: 'none',
              backgroundColor:
                selectedTableType === 'userOverrides'
                  ? colors.blue[800]
                  : 'lightGray',
            }}
            onClick={() => {
              setSelectedTableType('userOverrides');
            }}
          />
        </Box>
        <Box
          sx={{
            display: 'inline-grid',
            justifyContent: 'center',
          }}>
          <Chip
            variant="outlined"
            label="Rates by Role"
            style={{
              fontWeight: 'bold',
              // textTransform: 'uppercase',
              marginInline: 5,
              color: selectedTableType === 'roleRates' ? 'white' : 'black',
              backgroundColor:
                selectedTableType === 'roleRates'
                  ? colors.blue[800]
                  : 'lightGray',
              border: 'none',
            }}
            onClick={() => {
              setSelectedTableType('roleRates');
            }}
          />
        </Box>
      </Box>
      {selectedTableType === 'roleRates' && (
        <Box sx={{ mx: 3, mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <ImportLabourRoleRatesMenuButton onSelect={setImportRoleRatesScope} />
          <AddNewRateCardItemButton
            onClickAddBuyRate={() => {
              setEditCostRate(null);
              openModal(LabourRateModalsType.COST_RATE);
            }}
            onClickAddSellRate={() => {
              setEditBillingRate(null);
              openModal(LabourRateModalsType.BILLING_RATE);
            }}
            buyRateLabel="Add Cost Rate"
            sellRateLabel="Add Billing Rate"
          />
        </Box>
      )}
      <Box sx={{ mx: 3, my: 2, backgroundColor: '#fff', borderRadius: 1 }}>
        {selectedTableType === 'roleRates' && (
          <>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                p: '1rem',
              }}>
              <Typography variant="h6" sx={{ color: blueGrey[700] }}>
                Rates Per Hour by Role
              </Typography>
            </Stack>
            <Box sx={{ px: 2, pb: 2 }}>
              <LabourRatesTableComponent
                costRates={labourData.data?.costRates || []}
                billingRates={labourData.data?.billingRates || []}
                onRoleClick={handleRoleClick}
              />
            </Box>
          </>
        )}
        {selectedTableType === 'roleRates' && (
          <>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                p: '1rem',
              }}>
              <Typography variant="h6" sx={{ color: blueGrey[700] }}>
                Working Away From Home Rates Per Night by Role
              </Typography>
            </Stack>
            <Box sx={{ px: 2, pb: 2 }}>
              <WorkingAwayFromHomeRatesTableComponent
                costRates={labourData.data?.costRates || []}
                billingRates={labourData.data?.billingRates || []}
                onRoleClick={handleRoleClick}
              />
            </Box>
          </>
        )}
      </Box>
      {selectedTableType === 'userOverrides' && (
        <Box sx={{ m: 3, backgroundColor: '#fff', borderRadius: 1 }}>
          <Stack
            direction="row"
            sx={{
              p: '1rem',
            }}>
            <Typography variant="h6" sx={{ color: blueGrey[700] }}>
              Cost Rates Per Hour by Employee
            </Typography>
          </Stack>
          <Box sx={{ p: 2 }}>
            <LabourCostRateUserOverrideTable
              costRateUserOverrides={
                labourData.data?.costRateUserOverrides || []
              }
              isFetching={labourData.isFetching}
              onImportClick={() => setImportEmployeeCostOpen(true)}
              onRowClick={(rateId) => {
                const rate = labourData.data?.costRateUserOverrides.find(
                  (r) => r.id === rateId
                );
                if (rate) {
                  setSelectedUserCostOverride(rate);
                  openModal(LabourRateModalsType.COST_OVERRIDE_VIEW);
                }
              }}
              onAddNewClick={() => {
                setSelectedUserCostOverride(null);
                setEditCostRateOverride(null);
                openModal(LabourRateModalsType.COST_RATE_OVERRIDE);
              }}
            />
          </Box>
        </Box>
      )}
      <AddLabourRates
        handleCostRateFormClose={() => {
          setEditCostRate(null);
          closeModal(LabourRateModalsType.COST_RATE);
        }}
        handleCostRateOverrideFormClose={() => {
          setSelectedUserCostOverride(null);
          closeModal(LabourRateModalsType.COST_RATE_OVERRIDE);
        }}
        handleBillingRateFormClose={() => {
          setEditBillingRate(null);
          closeModal(LabourRateModalsType.BILLING_RATE);
        }}
        openCostRateForm={modalState[LabourRateModalsType.COST_RATE]}
        openCostRateOverrideForm={
          modalState[LabourRateModalsType.COST_RATE_OVERRIDE]
        }
        openBillingRateForm={modalState[LabourRateModalsType.BILLING_RATE]}
        editCostRate={editCostRate}
        editCostRateOverride={editCostRateOverride}
        editBillingRate={editBillingRate}
        onClearEditCostRate={() => setEditCostRate(null)}
        onClearEditCostRateOverride={() => setEditCostRateOverride(null)}
        onClearEditBillingRate={() => setEditBillingRate(null)}
      />

      {modalState[LabourRateModalsType.DETAILS_VIEW] && (
        <LabourDetailsView
          handleClose={() => closeModal(LabourRateModalsType.DETAILS_VIEW)}
          selectedRole={selectedRole}
          rateType={selectedRateType}
          open={modalState[LabourRateModalsType.DETAILS_VIEW]}
          onEditCostRate={(rate) => {
            setEditCostRate(rate);
            closeModal(LabourRateModalsType.DETAILS_VIEW);
            openModal(LabourRateModalsType.COST_RATE);
          }}
          onEditBillingRate={(rate) => {
            setEditBillingRate(rate);
            closeModal(LabourRateModalsType.DETAILS_VIEW);
            openModal(LabourRateModalsType.BILLING_RATE);
          }}
        />
      )}

      {modalState[LabourRateModalsType.COST_OVERRIDE_VIEW] && (
        <LabourCostOverrideDetailsView
          handleClose={() =>
            closeModal(LabourRateModalsType.COST_OVERRIDE_VIEW)
          }
          selectedUser={selectedUserCostOverride?.user}
          open={modalState[LabourRateModalsType.COST_OVERRIDE_VIEW]}
          onEditCostRateOverride={(rate) => {
            setEditCostRateOverride(rate);
            closeModal(LabourRateModalsType.COST_OVERRIDE_VIEW);
            openModal(LabourRateModalsType.COST_RATE_OVERRIDE);
          }}
        />
      )}

      {importRoleRatesScope === 'default' && (
        <BulkUpdateRoleRatesDialog
          open
          handleClose={() => setImportRoleRatesScope(null)}
        />
      )}
      {importRoleRatesScope === 'scoped' && (
        <BulkUpdateScopedBillingRatesDialog
          open
          handleClose={() => setImportRoleRatesScope(null)}
        />
      )}
      {importEmployeeCostOpen && (
        <BulkUpdateEmployeeCostRatesDialog
          open
          handleClose={() => setImportEmployeeCostOpen(false)}
        />
      )}
    </>
  );
}
